import React, { useState, useEffect } from "react";
import { API_BASE_URL, buildApiUrl } from "../config/api";
import axios from "axios";
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  CardTitle,
  Row,
  Col,
  Table,
  Form,
  FormGroup,
  Input,
  Button,Modal, ModalHeader, ModalBody, ModalFooter
} from "reactstrap";

const BACKEND_BASE_URL = API_BASE_URL;
const TITLE_SECTION_IDS_STORAGE_KEY = "homePage.titleSectionIds";

const TITLE_SECTION_CONFIG = [
  {
    key: "shop",
    backgroundName: "Shop",
    fallbackNameAr: "المتجر",
    cardTitle: "Tank Shop Title",
    buttonLabel: "Update Tank Shop Title",
    hideImageFields: true,
  },
  {
    key: "announcements",
    backgroundName: "Announcements",
    fallbackNameAr: "الإعلانات",
    cardTitle: "Announcements Title",
    buttonLabel: "Update Announcements Title",
    hideImageFields: true,
  },
  {
    key: "gallery",
    backgroundName: "Gallery",
    fallbackNameAr: "المعرض",
    cardTitle: "Exposition Title",
    buttonLabel: "Update Exposition Title",
    hideImageFields: true,
  },
];

const TITLE_SECTIONS_WITHOUT_IMAGES = new Set(
  TITLE_SECTION_CONFIG.filter((section) => section.hideImageFields).map(
    (section) => section.key
  )
);

const normalizeLookupValue = (value) => String(value || "").trim().toLowerCase();

const getStoredTitleSectionIds = () => {
  if (typeof window === "undefined") {
    return {};
  }

  try {
    const rawValue = window.localStorage.getItem(TITLE_SECTION_IDS_STORAGE_KEY);
    const parsedValue = rawValue ? JSON.parse(rawValue) : {};
    return parsedValue && typeof parsedValue === "object" ? parsedValue : {};
  } catch (error) {
    return {};
  }
};

const persistTitleSectionIds = (nextIds) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(
      TITLE_SECTION_IDS_STORAGE_KEY,
      JSON.stringify(nextIds || {})
    );
  } catch (error) {}
};

const resolveTitleSectionKey = (background, storedIds = {}) => {
  const backgroundId = Number(background?.id);
  if (backgroundId) {
    const matchedById = TITLE_SECTION_CONFIG.find(
      (section) => Number(storedIds?.[section.key]) === backgroundId
    );
    if (matchedById) {
      return matchedById.key;
    }
  }

  const normalizedName = normalizeLookupValue(background?.name);
  const matchedByName = TITLE_SECTION_CONFIG.find(
    (section) => normalizedName === normalizeLookupValue(section.backgroundName)
  );

  return matchedByName?.key || "";
};

const resolveAssetUrl = (path) => {
  if (!path) {
    return "";
  }

  return buildApiUrl(path);
};

function HomePage() {
  const [backgrounds, setBackgrounds] = useState([]);
  const [editingBackground, setEditingBackground] = useState(null);
  const [backgroundCount, setBackgroundCount] = useState(0);
  const [newImage, setNewImage] = useState(null);
  const [newIcon, setNewIcon] = useState(null); 
  const [addError, setAddError] = useState(""); 
  const [addMessageError, setaddMessageError] = useState("");
  const [updateMessageError, setupdateMessageError] = useState("");
  const [updateError, setUpdateError] = useState(""); // State for update error message
  const [hasFocused, setHasFocused] = useState(false);
  const [addSuccessMessage, setAddSuccessMessage] = useState("");
  const [updateSuccessMessage, setUpdateSuccessMessage] = useState("");
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [deleteBackgroundId, setDeleteBackgroundId] = useState(null);
  const [titleItems, setTitleItems] = useState({});
  const [titleMessages, setTitleMessages] = useState({});
  const [orderMessage, setOrderMessage] = useState({ error: "", success: "" });

  const isTextOnlySection = (background) =>
    TITLE_SECTIONS_WITHOUT_IMAGES.has(
      normalizeLookupValue(
        background?.sectionKey || resolveTitleSectionKey(background, getStoredTitleSectionIds())
      )
    );


  // Function to check if the image extension is valid
  const isValidImageExtension = (fileName) => {
    const validExtensions = ['.webp', '.gif', '.png', '.jpg', '.jpeg'];
    const fileExtension = fileName.slice(((fileName.lastIndexOf(".") - 1) >>> 0) + 2).toLowerCase();
    return validExtensions.includes(`.${fileExtension}`);
  };

  const fetchBackgrounds = async () => {
    try {
      const response = await axios.get(`${BACKEND_BASE_URL}/background/list`);
      if (response.data.success) {
        const storedTitleSectionIds = getStoredTitleSectionIds();
        const nextStoredTitleSectionIds = { ...storedTitleSectionIds };
        const incomingBackgrounds = Array.isArray(response.data.data) ? response.data.data : [];

        const mappedBackgrounds = incomingBackgrounds.map((item) => {
          const sectionKey = resolveTitleSectionKey(item, storedTitleSectionIds);
          if (sectionKey && item?.id) {
            nextStoredTitleSectionIds[sectionKey] = item.id;
          }

          return {
            ...item,
            is_active: Number(item?.is_active ?? 1) === 0 ? 0 : 1,
            sectionKey,
          };
        });

        persistTitleSectionIds(nextStoredTitleSectionIds);
        setBackgrounds(mappedBackgrounds);
        setBackgroundCount(mappedBackgrounds.length);
        const mappedTitleItems = TITLE_SECTION_CONFIG.reduce((acc, section) => {
          const currentItem =
            mappedBackgrounds.find(
              (item) => Number(item.id) === Number(nextStoredTitleSectionIds[section.key])
            ) ||
            mappedBackgrounds.find((item) => item.sectionKey === section.key) ||
            null;

          acc[section.key] = currentItem ? { ...currentItem, sectionKey: section.key } : null;
          return acc;
        }, {});
        setTitleItems(mappedTitleItems);
      } else {
        console.error("API request was not successful:", response.data.errors);
      }
    } catch (error) {
      console.error("Error fetching backgrounds:", error);
    }
  };

  const moveBackground = async (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= backgrounds.length) {
      return;
    }

    const previousBackgrounds = backgrounds;
    const reordered = [...backgrounds];
    [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
    setBackgrounds(reordered);
    setOrderMessage({ error: "", success: "" });

    try {
      const response = await axios.put(`${BACKEND_BASE_URL}/background/order`, {
        ids: reordered.map((item) => item.id),
      });
      if (!response.data?.success) {
        throw new Error(response.data?.errors?.message || "Failed to update section order");
      }
      setOrderMessage({ error: "", success: "Section order saved successfully" });
      setTimeout(() => setOrderMessage({ error: "", success: "" }), 3000);
    } catch (error) {
      setBackgrounds(previousBackgrounds);
      setOrderMessage({
        error: error.response?.data?.errors?.message || error.message || "Failed to update section order",
        success: "",
      });
    }
  };

  const addBackground = async () => {
    const name = document.getElementById("newBackgroundName").value.trim();
    const text = document.getElementById("newBackgroundText").value.trim();
    const imageFile = document.getElementById("newBackgroundImage").files[0];
    const iconFile = document.getElementById("newBackgroundIcon").files[0];
  
    // Check if all fields are filled
    if (!name || !text || !imageFile || !iconFile) {
      setaddMessageError("All fields are required.");
      setTimeout(() => {
        setaddMessageError("");
      }, 5000);
      return;
    }
  

    // Validate image and icon extensions
    if ((imageFile && !isValidImageExtension(imageFile.name)) ||
        (iconFile && !isValidImageExtension(iconFile.name))) {
        setAddError("Invalid file extension. Please upload .WEBP, .GIF, .PNG, .JPG, or .JPEG");
        setTimeout(() => {
          setAddError("");
        }, 5000);
        return;
    }
    setAddError(""); // Clear the error message

    try {
        const formData = new FormData();
        formData.append("name", document.getElementById("newBackgroundName").value);
        formData.append("text", document.getElementById("newBackgroundText").value);
        formData.append("image_path", imageFile);
        formData.append("icon_path", iconFile);

        await axios.post(`${BACKEND_BASE_URL}/background/add`, formData);
        fetchBackgrounds();
        setAddSuccessMessage("Background added successfully");
        setTimeout(() => {
          setAddSuccessMessage("");
        }, 5000);
        document.getElementById("newBackgroundName").value = "";
        document.getElementById("newBackgroundText").value = "";
        document.getElementById("newBackgroundImage").value = "";
        document.getElementById("newBackgroundIcon").value = "";
    } catch (error) {
        console.error("Error adding background:", error);
    }
};


const updateBackground = async (id) => {
  const formData = new FormData();
  let isFieldUpdated = false;
  const shouldHideImageFields = isTextOnlySection(editingBackground);

  // Validation for name and text
  if (!editingBackground.name.trim() || !editingBackground.name_ar.trim() || !editingBackground.text.trim() || !editingBackground.text_ar.trim()) {
    setupdateMessageError("Name and Text fields are required.");
    setTimeout(() => {
      setupdateMessageError("");
    }, 5000);
    return;
  }

  // Append name and text to formData
  formData.append("name", editingBackground.name);
  formData.append("name_ar", editingBackground.name_ar);
  formData.append("text", editingBackground.text);
  formData.append("text_ar", editingBackground.text_ar);
  formData.append("is_active", Number(editingBackground.is_active ?? 1) === 0 ? 0 : 1);

  // Append new image if provided
  if (!shouldHideImageFields && newImage) {
    if (!isValidImageExtension(newImage.name)) {
      setUpdateError("Invalid file extension for image. Allowed: .WEBP, .GIF, .PNG, .JPG, .JPEG");
      setTimeout(() => setUpdateError(""), 5000);
      return;
    }
    formData.append("image_path", newImage);
    isFieldUpdated = true;
  }

  // Append new icon if provided
  if (!shouldHideImageFields && newIcon) {
    if (!isValidImageExtension(newIcon.name)) {
      setUpdateError("Invalid file extension for icon. Allowed: .WEBP, .GIF, .PNG, .JPG, .JPEG");
      setTimeout(() => setUpdateError(""), 5000);
      return;
    }
    formData.append("icon_path", newIcon);
    isFieldUpdated = true;
  }

  // Make the update request
  try {
    const response = await axios.put(`${BACKEND_BASE_URL}/background/update/${id}`, formData);
    if (response.data.success) {
      fetchBackgrounds();
      setEditingBackground({ name: "", name_ar: "", text: "", text_ar: "" });
      setNewImage(null);
      setNewIcon(null);
      setUpdateSuccessMessage("HomePage updated successfully");
      setTimeout(() => {
        setUpdateSuccessMessage("");
      }, 5000);
    } else {
      console.error("Failed to update background:", response.data.errors);
      setUpdateError(response.data.errors);
    }
  } catch (error) {
    console.error("Error updating background:", error);
    setUpdateError("Error occurred while updating");
  }
};

const setTitleMessage = (key, nextValue) => {
  setTitleMessages((prev) => ({
    ...prev,
    [key]: {
      error: "",
      success: "",
      ...(prev[key] || {}),
      ...nextValue,
    },
  }));
};

const clearTitleMessage = (key, field) => {
  setTimeout(() => {
    setTitleMessages((prev) => ({
      ...prev,
      [key]: {
        error: "",
        success: "",
        ...(prev[key] || {}),
        [field]: "",
      },
    }));
  }, 5000);
};

const updateTitleItemField = (key, field, value) => {
  setTitleItems((prev) => {
    const currentItem = prev[key];
    if (!currentItem) {
      return prev;
    }

    return {
      ...prev,
      [key]: {
        ...currentItem,
        [field]: value,
      },
    };
  });
};

const updateTitleSection = async (section) => {
  const titleItem = titleItems[section.key];

  if (!titleItem) {
    return;
  }

  if (!titleItem.text?.trim() || !titleItem.text_ar?.trim()) {
    setTitleMessage(section.key, {
      error: "English and Arabic titles are required.",
      success: "",
    });
    clearTitleMessage(section.key, "error");
    return;
  }

  try {
    const formData = new FormData();
    formData.append("name", titleItem.name || section.backgroundName);
    formData.append("name_ar", titleItem.name_ar || section.fallbackNameAr);
    formData.append("text", titleItem.text);
    formData.append("text_ar", titleItem.text_ar);

    const response = await axios.put(
      `${BACKEND_BASE_URL}/background/update/${titleItem.id}`,
      formData
    );

    if (response.data.success) {
      await fetchBackgrounds();
      setTitleMessage(section.key, {
        error: "",
        success: `${section.cardTitle} updated successfully`,
      });
      clearTitleMessage(section.key, "success");
    } else {
      setTitleMessage(section.key, {
        error: `Failed to update ${section.cardTitle}`,
        success: "",
      });
      clearTitleMessage(section.key, "error");
    }
  } catch (error) {
    console.error(`Error updating ${section.cardTitle}:`, error);
    setTitleMessage(section.key, {
      error: `Error occurred while updating ${section.cardTitle}`,
      success: "",
    });
    clearTitleMessage(section.key, "error");
  }
};




  
const deleteBackground = (id) => {
  setDeleteBackgroundId(id);
  setShowDeletePopup(true);
};

  

const performDelete = async () => {
  try {
    const response = await axios.delete(`${BACKEND_BASE_URL}/background/delete/${deleteBackgroundId}`);
    if (response.status === 200) {
      console.log('Background deleted successfully');
      fetchBackgrounds();
    } else {
      console.error('Delete request failed:', response.status);
    }
  } catch (error) {
    console.error('Error deleting background:', error);
  }
  setShowDeletePopup(false);
};




useEffect(() => {
  fetchBackgrounds();
  if (editingBackground && !hasFocused) {
    const updateNameInputField = document.getElementById('update-background-name');
    if (updateNameInputField) {
      updateNameInputField.focus();
      setHasFocused(true);
    }
  }

  // Reset focus state when editingBackground is set to null (e.g., after update or cancel)
  if (!editingBackground) {
    setHasFocused(false);
  }
}, [editingBackground]);

const shouldHideImageFields = isTextOnlySection(editingBackground);
  

  return (
    <>
  <div className="content">
        <Row>
          <Col md="12">
            <Card style={{ backgroundColor: '#CFCFCF' }}>
              <CardHeader>
                <CardTitle tag="h4">Home Page</CardTitle>
              </CardHeader>
              <CardBody >
                
                <CardFooter>
                  <div>Total backgrounds: {backgroundCount}</div>
                </CardFooter>
                <Table responsive >
                  <thead className="text-primary">
                    <tr>
                      {/* <th>Name</th>
                      <th>Arabic Name</th> */}
                      <th>Name</th>
                      <th>Arabic Name</th>
                      <th>Active</th>
                      <th>Order</th>
                      <th>Image</th>
                      {/* <th>Icon</th> */}
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
  {backgrounds.map((background, index) => (
    <tr key={background.id}>
      {/* <td>{background.name}</td>
      <td>{background.name_ar}</td> */}
      <td>{background.text}</td>
      <td>{background.text_ar}</td>
      <td>{Number(background.is_active ?? 1) === 0 ? "Inactive" : "Active"}</td>
      <td style={{ whiteSpace: "nowrap" }}>
        <Button
          color="secondary"
          size="sm"
          disabled={index === 0}
          onClick={() => moveBackground(index, -1)}
          aria-label={`Move ${background.text || background.name} up`}
        >
          ↑
        </Button>{" "}
        <Button
          color="secondary"
          size="sm"
          disabled={index === backgrounds.length - 1}
          onClick={() => moveBackground(index, 1)}
          aria-label={`Move ${background.text || background.name} down`}
        >
          ↓
        </Button>
      </td>
      <td>
        {background.image_path && (
          <img
            src={resolveAssetUrl(background.image_path)}
            alt="Background"
            style={{ maxWidth: "100px" }}
          />
        )}
      </td>
      {/* <td>
        {background.icon_path && (
          <img
            src={`${BACKEND_BASE_URL}/uploads/${background.icon_path}`}
            alt="Icon"
            style={{ maxWidth: "100px" }}
          />
        )}
      </td> */}
      <td>
        <Button
          color="success"
          onClick={() => {
            setEditingBackground(background);
            setNewImage(null);
            setNewIcon(null);
          }}
        >
          Update
        </Button>{" "}
        {/* <Button
          color="danger"
          onClick={() => deleteBackground(background.id)}
        >
          Delete
        </Button> */}
      </td>
    </tr>
  ))}
</tbody>

                </Table>
                {orderMessage.error && (
                  <div className="alert alert-danger" role="alert">{orderMessage.error}</div>
                )}
                {orderMessage.success && (
                  <div className="alert alert-success" role="alert">{orderMessage.success}</div>
                )}
              </CardBody>
              {editingBackground && (
  <CardFooter>
    <Form>
      <Row>
        <Col md="5">
          <FormGroup>
            <label>Update Text:</label>
            <Input
              type="text"
              name="text"
              value={editingBackground.text}
              onChange={(e) =>
                setEditingBackground({
                  ...editingBackground,
                  text: e.target.value,
                })
              }
            />
          </FormGroup>
        </Col>
        <Col md="5">
          <FormGroup>
            <label>Update Arabic Text:</label>
            <Input
              type="text"
              name="text_ar"
              value={editingBackground.text_ar}
              onChange={(e) =>
                setEditingBackground({
                  ...editingBackground,
                  text_ar: e.target.value,
                })
              }
            />
          </FormGroup>
        </Col>
        <Col md="2">
          <FormGroup>
            <label>Status:</label>
            <Input
              type="select"
              name="is_active"
              value={Number(editingBackground.is_active ?? 1)}
              onChange={(e) =>
                setEditingBackground({
                  ...editingBackground,
                  is_active: Number(e.target.value),
                })
              }
            >
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </Input>
          </FormGroup>
        </Col>
        {!shouldHideImageFields && (
        <Col md="8">
          <FormGroup>
            <label>Current Image:</label>
            {editingBackground.image_path && (
              <div>
                <img
                  src={resolveAssetUrl(editingBackground.image_path)}
                  alt="Background Image"
                  style={{ maxWidth: '200px', marginBottom: '10px' }}
                />
              </div>
            )}
            <label>Update Image (optional):</label>
            <Input
              type="file"
              name="image"
              onChange={(e) => setNewImage(e.target.files[0])}
            />
          </FormGroup>
        </Col>
        )}
        <Col md="8">
          {/* <FormGroup>
            <label>Current Icon:</label>
            {editingBackground.icon_path && (
              <div>
                <img
                  src={`${BACKEND_BASE_URL}/uploads/${editingBackground.icon_path}`}
                  alt="Background Icon"
                  style={{ maxWidth: '100px', marginBottom: '10px' }}
                />
              </div>
            )}
            <label>Update Icon (optional):</label>
            <Input
              type="file"
              name="icon"
              onChange={(e) => setNewIcon(e.target.files[0])}
            />
          </FormGroup> */}
        </Col>
        <Col md="12">
          <Button
            className="btn-round"
            color="info"
            onClick={() => updateBackground(editingBackground.id)}
          >
            Update HomePage
          </Button>{" "}
          <Button
            className="btn-round"
            color="secondary"
            onClick={() => setEditingBackground(null)}
          >
            Cancel
          </Button> {" "} <br />
          {updateError && (
          <div style={{ color: 'red', marginTop: '10px' }}>
            {updateError}
          </div>
        )}
        {updateSuccessMessage && (
        <div className="alert alert-success" role="alert" style={{ width: '50%', margin: '10px auto', textAlign: 'center'  }}>
          {updateSuccessMessage}
        </div>
      )}
      {updateMessageError && (
    <div className="alert alert-danger" role="alert" style={{ width: '50%', margin: '10px auto', textAlign: 'center'  }}>
      {updateMessageError}
    </div>
  )}
        </Col>
      </Row>
    </Form>
  </CardFooter>
)}

            </Card>
          </Col>
        </Row>
        <Modal isOpen={showDeletePopup} toggle={() => setShowDeletePopup(false)}>
  <ModalHeader toggle={() => setShowDeletePopup(false)}>Confirm Delete</ModalHeader>
  <ModalBody>
    Are you sure you want to delete this background?
  </ModalBody>
  <ModalFooter>
    <Button color="danger" onClick={() => performDelete()}>Delete</Button>{' '}
    <Button color="secondary" onClick={() => setShowDeletePopup(false)}>Cancel</Button>
  </ModalFooter>
</Modal>
      </div>
    </>
  );
}

export default HomePage;
